// core
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, memo } from 'react';
import mergeRefs from 'react-merge-refs';
import useMeasure from 'react-use-measure';
import _ from 'lodash';
import { useGlobalStore } from '@rs-core/store/globalStore';
import fhirExtensionUrls from '@worklist-2/core/src/fhir/extension/fhirExtensionUrls';
import { useRouter } from '@rs-core/hooks/useRouter';
import { CSVLink } from 'react-csv';
import { utils, writeFile } from 'xlsx';
import getUserFullName from '@worklist-2/core/src/fhir/resource/columnMapping/utils/getUserFullName';
import { useTranslation } from 'react-i18next';
import GroupingDropable from './GroupingDropable';
import WorklistContextMenu from '../Worklist/WorklistGrid/WorklistContextMenu';
import { useSelection } from '@rs-core/context/SelectionContext';

// MUI
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';

// TanStack
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
	getExpandedRowModel,
} from '@tanstack/react-table';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useVirtualizer, notUndefined } from '@tanstack/react-virtual';

// React DnD
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Components
import { MemoizedDataGridColumnHeaderV2 } from './DataGridColumnHeaderV2';
import DataGridRow from './DataGridRow';
import { MemoizedDataGridRowGroup } from './DataGridRowGroup';
import DataGridColumnDragLayer from './DataGridColumnDragLayer';

// utils
import { clearDuplicates, findIdenticalItems, hasAtLeastOneNonIdenticalItem } from '../Worklist/WorklistGrid/utils';
import { getViewerURLs } from '../Worklist/WorklistGrid/getViewerURLs';
import { getTextWidth } from '@rs-ui/helpers/utils';
import {
	bulidFilterForGroup,
	getLastGroupByContentRecord,
	compareGroupContent,
} from '@rs-ui/components/utils/gridUtils';

// libraries
import PropTypes from 'prop-types';
import { useBooleanFlagValue } from '@rs-core/hooks/useFlags';
import { useNewWorklistToggleContext } from '@worklist-2/core/src/context/NewWorklistToggleContext';

const DataGrid = forwardRef(
	(
		{
			name,
			cacheKey,
			columns,
			setCurrWorklistColumns,
			height,
			pageSize,
			canResizeColumns,
			selectedRow,
			enableRowDnd,
			enableColumnDnd,
			setTotalRecords,
			onRowClick,
			onRowDoubleClick,
			onLoadData,
			onLoadDataRef,
			selectedResources,
			shouldUpdateAfterFiltersChanged,
			onUpdateAfterFiltersChanged,
			columnSizing,
			setColumnSizing,
			cellsPadding,
			defaultColumnWidth,
			subRow,
			subRowSX,
			disableDataCache,
			expandedRowIndex,
			footer,
			cellSx,
			forceFixed,
			setEnableDownloadButton,
			visitedWorklist,
			gridCustomHeader,
			onScrollChange,
			scrollPosition,
			groupingItems,
			isLoadingRef,
			wheelMenuItems = [],
			handleWheelClick,
			onDataGridContextMenuClick,
		},
		ref
	) => {
		const { t } = useTranslation('root');
		const { toggleGetFeatureFlagValue } = useNewWorklistToggleContext();

		const mavenWorklistGrouping = toggleGetFeatureFlagValue('maven-worklist-grouping');
		const rowColorStudyStatus = useBooleanFlagValue('maven-2726-row-colors-study-status');
		const mavenEnableWorklistContextMenu = toggleGetFeatureFlagValue('maven-2735-worklist-right-click-menu');

		//we need a reference to the scrolling element for logic down below
		const tableContainerRef = useRef(null);
		//Reference to the calculate position of progress bar
		const tableHeaderRef = useRef(null);
		// Ref for virtualized rows scroll div
		const vRowRef = useRef(null);

		// useMeasure requires its own seperate ref: https://www.npmjs.com/package/react-use-measure#multiple-refs
		const [tableMeasurementsRef, tableMeasurements] = useMeasure();
		const [hasMoreData, setHasMoreData] = useState(false);
		const [forceRefresh, setForceRefresh] = useState(false);
		const [lastColId, setLastColId] = useState('');
		const [lastColWidth, setLastColWidth] = useState(0);
		const [secondLastChildResizerOpacity, setSecondLastChildResizerOpacity] = useState(0);
		const { goTo } = useRouter();

		// stack of selected items at the top of the grid.
		// we should manage it here because we manage data-flow inside of this component
		const stickyStack = useRef(null);

		// use for managing downloading grid data
		const csvLinkRef = useRef();

		const [exportHeader, setExportHeader] = useState([]);
		const [exportData, setExportData] = useState([]);
		const [isRowLoading, setIsRowLoading] = useState(false);

		// Grouping
		const [currentExpandedGroup, setCurrentExpandedGroup] = useState([]);
		const expandRowRef = useRef([]);
		// Message shown when there is no data in the grid
		const [emptyMessage, setEmptyMessage] = useState(null);
		// Used to check if the component has rendered
		const [isRendered, setIsRendered] = useState(false);

		const afterKeyRef = useRef(null);

		const [points, setPoints] = useState({
			x: 0,
			y: 0,
		});

		const [rightClickedItem, setRightClickedItem] = useState(null);

		const { toggleStudyPresence } = useSelection();

		useEffect(() => {
			// Restore scroll position on mount
			if (isRendered && scrollPosition) {
				const currentRef = vRowRef;

				currentRef.current.scrollTop = scrollPosition.scrollTop;
				currentRef.current.scrollLeft = scrollPosition.scrollLeft;
			}
		}, [isRendered]);

		// Reset scroll position on cacheKey change (e.g. when the user changes filters or sorts)
		useEffect(() => {
			if (cacheKey) {
				const currentRef = vRowRef;

				currentRef.current.scrollTop = 0;

				if (scrollPosition?.scrollLeft === 0) {
					currentRef.current.scrollLeft = 0;
				}
			}

			setCurrentExpandedGroup([]);
			expandRowRef.current = [];
			afterKeyRef.current = null;
		}, [cacheKey]);

		const handleExport = fileType => {
			const { rows: exportedRows } = table.getRowModel();
			if (exportedRows?.length >= 0) {
				const physicianList = [
					'performingTechnologist',
					'readingPhysician',
					'referringPhysician',
					'transcriptionist',
				];
				// get csv header from columns, remove id and columsn without values
				const exportHeaders = columns
					.filter(c => !c.options.excludeFromSave)
					.map(c => ({ label: c.label, key: c.name }));

				// get values from _valuesCache
				const arr = [];

				exportedRows.forEach(row => {
					const arrObj = {};
					row.getVisibleCells().forEach((cell, index) => {
						if (cell.column.columnDef.visible && !cell.column.columnDef.options.excludeFromSave) {
							const useCustomExportFn = cell.column.columnDef.options?.useCustomExportFn;
							const cellObj = flexRender(cell.column.columnDef.cell, cell.getContext());
							arrObj[cellObj?.props?.column?.id] = useCustomExportFn
								? cell.column.accessorFn(cell.row.original, index, cell.column)
								: cellObj?.props?.cell?.getValue();
						}
					});

					// need to remove ^ from physician names
					physicianList.forEach(physician => {
						if (arrObj[physician]) {
							arrObj[physician] = getUserFullName(arrObj[physician]);
						}
					});
					arr.push(arrObj);
				});
				if (fileType?.toLowerCase() === 'csv') {
					// set Exportdata and Header will trigger the download in the useEffect below
					setExportHeader(exportHeaders);
					setExportData(arr);
				} else if (fileType?.toLowerCase() === 'xlsx') {
					const xlsxHeaders = [];
					exportHeaders.forEach(header => {
						xlsxHeaders.push(header.label);
					});
					const worksheet = utils.json_to_sheet(arr);

					utils.sheet_add_aoa(worksheet, [xlsxHeaders]);
					worksheet['!cols'] = calculateXlsxHeaderWidth(arr, xlsxHeaders);

					const wb = utils.book_new();
					utils.book_append_sheet(wb, worksheet, 'Sheet1');

					writeFile(
						wb,
						`${_.upperFirst(name)} - ${new Date().toLocaleDateString().replaceAll('/', '-')}.xlsx`
					);
				}
			}
		};

		// function to calculate xlsx column width based on the header and data
		const calculateXlsxHeaderWidth = (arr, headers) => {
			const MAX_HEADER_WIDTH = 30;
			const wscols = Array(Object.keys(headers).length).fill({ width: 10 });

			headers.forEach((key, index) => {
				if (key && key.length > 0) {
					wscols[index] = {
						width: Math.min(Math.max(key.length + 1, wscols[index].width), MAX_HEADER_WIDTH),
					};
				}
			});
			arr.forEach(row => {
				Object.keys(row).forEach((key, index) => {
					if (typeof row[key] === 'number') {
						wscols[index] = { width: Math.min(Math.max(10, wscols[index].width), MAX_HEADER_WIDTH) };
					} else if (row[key] && row[key].length > 0) {
						wscols[index] = {
							width: Math.min(Math.max(row[key].length + 2, wscols[index].width), MAX_HEADER_WIDTH),
						};
					}
				});
			});

			return wscols;
		};

		// when exportHeader is updated, trigger the download
		useEffect(() => {
			if (csvLinkRef && csvLinkRef.current && exportHeader.length > 0) {
				csvLinkRef.current.link.click();
			}
		}, [exportHeader]);

		// Set empty message for each kind of grid
		useEffect(() => {
			if (name === 'teaching-folder') {
				setEmptyMessage(t('No study saved'));
			}
		}, [name]);

		const columnHelper = createColumnHelper();
		const cols = useMemo(() => {
			let columnList = [...columns];

			// If grouping items are present, hide them from the column list
			if (mavenWorklistGrouping && groupingItems && groupingItems.length > 0) {
				columnList = columnList.map(c => {
					const group = groupingItems.find(g => g.id === c.name);
					if (group) {
						return {
							...c,
							meta: { ...c.meta, visible: 'excluded' },
						};
					}
					return c;
				});

				// Add a space column to the beginning of the list to make room for the grouping columns
				const groupSpace = {
					name: 'groupspace',
					label: ' ',
					getDisplay: () => '',
					options: {
						viewColumns: false,
						draggable: false,
						groupable: false,
					},
					meta: { draggable: false },
					size: 33 * groupingItems.length - 12,
					disableResizing: true,
				};

				// Add the groupSpace column after the flag column
				columnList.splice(2, 0, groupSpace);
			}

			return columnList.map(c => {
				const labelWidth = getTextWidth(c.label, 'bold 14px Roboto');
				const width = labelWidth > 200 ? labelWidth : 200;

				return columnHelper.accessor(c.name, {
					cell: c.cell ? c.cell : info => info.getValue() || '',
					header: c.header || c.label || '',
					id: c.name,
					// https://tanstack.com/table/v8/docs/guide/column-defs#accessor-functions
					accessorFn: c.accessorFn,
					draggable: typeof c.meta?.draggable === 'undefined' ? true : c.meta?.draggable,
					visible: c.meta?.visible !== 'excluded',
					sortingFn: c.sortingFn,
					options: c.options,
					size: columnSizing[c.name] || c.size || defaultColumnWidth || width,
					minSize: c.minSize || c.size || defaultColumnWidth || 104, // 104 is the min size so that the sort button still looks ok
					maxSize: c.maxSize || 3000, // arbitrary size
					disableResizing: c.disableResizing,
				});
			});
		}, [columns, columnSizing, defaultColumnWidth, groupingItems]);

		// Setting initial order for columns
		const [columnOrder, setColumnOrder] = useState([]);

		// When columns (order, addition, or deletion ) changes
		useEffect(() => {
			setColumnOrder(cols.map(column => column.id));
		}, [cols]);

		const {
			data: tableData,
			isFetchingNextPage,
			fetchNextPage,
			isFetching,
			isLoading,
			refetch,
		} = useInfiniteQuery(
			['table-data', name, ...[cacheKey]],
			async ({ pageParam = 1 }) => {
				const fetchedData = onLoadDataRef
					? await onLoadDataRef.current(pageParam, pageSize, null, null, afterKeyRef.current)
					: await onLoadData(pageParam, pageSize);

				if (mavenWorklistGrouping && groupingItems && groupingItems.length > 0) {
					let hasMore = false;
					// In the first page, we need to check if the data is less than pageSize -1
					// because for the first record has empty value, the database can has empty value or null value.
					// So API will merge the two records and return only one record and return the pageSize -1 records.
					if (pageParam === 1) {
						hasMore = fetchedData?.length >= pageSize - 1;
					} else {
						hasMore = fetchedData?.length === pageSize;
					}
					setHasMoreData(hasMore);

					// Add the afterKey to the last record of the page
					// The grouping grid needs to know the afterKey of the last record to fetch the next page
					if (hasMore) {
						afterKeyRef.current = fetchedData[fetchedData.length - 1]?.rawData?.afterKey;
					} else {
						afterKeyRef.current = null;
					}
				} else {
					setHasMoreData(fetchedData?.length === pageSize);
				}

				if (setEnableDownloadButton) {
					setEnableDownloadButton(true);
				}
				return fetchedData;
			},
			{
				getNextPageParam: (lastPage, allPages) => (lastPage?.length > 0 ? allPages.length + 1 : undefined),
				cacheTime: disableDataCache ? 0 : undefined,
				keepPreviousData: true,
				refetchOnWindowFocus: false,
			}
		);

		const command = useGlobalStore(state => state.command);
		const setCommand = useGlobalStore(state => state.setCommands);
		const setReceivedTranscription = useGlobalStore(state => state.setReceivedTranscription);

		//we must flatten the array of arrays from the useInfiniteQuery hook
		const flatData = useMemo(() => {
			let result = tableData?.pages?.flatMap(page => page) ?? [];

			const _result = clearDuplicates(result, stickyStack.current || []);
			result = [...(stickyStack.current || []), ..._result];

			return result;
		}, [tableData, forceRefresh]);

		useEffect(() => {
			const wasItemRemoved = hasAtLeastOneNonIdenticalItem(stickyStack.current || [], selectedResources);

			if (wasItemRemoved) {
				stickyStack.current = findIdenticalItems(selectedResources, stickyStack.current || []);
				setForceRefresh(val => !val);
			} else if (shouldUpdateAfterFiltersChanged) {
				stickyStack.current = [...selectedResources];
				onUpdateAfterFiltersChanged(false);
			}
		}, [shouldUpdateAfterFiltersChanged, selectedResources]);

		const selectedResourcesRowsIds = useMemo(() => {
			const duplicateIndexes = [];

			flatData?.forEach((item1, index1) => {
				selectedResources?.forEach(item2 => {
					if (item1.studyID === item2.studyID) {
						duplicateIndexes.push(index1);
					}
				});
			});

			return duplicateIndexes;
		}, [flatData, selectedResources]);

		// Set total # of rows
		setTotalRecords?.(flatData.length);

		const getRowIndexOf = (colName, value) => _.findIndex(flatData, el => el[colName] === value);

		const getRowData = (colName, value) => _.find(flatData, el => el[colName] === value);

		const updateData = (rowIndex, colName, value, extensionUrl, resourceId) => {
			const page = Math.floor(rowIndex / pageSize);
			const pageRowIdx = rowIndex % pageSize;

			let record = tableData?.pages[page][pageRowIdx] || rows[rowIndex];
			// The index can be changed when the data is updated, so we need to find the correct row
			// If resourceId is provided, we need to update the row with that resourceId
			if (resourceId) {
				let nextIndex = rowIndex;
				while (record?.id !== resourceId && nextIndex > 0) {
					nextIndex -= 1;
					const nextPage = Math.floor(nextIndex / pageSize);
					const nextPageRowIdx = nextIndex % pageSize;

					record = tableData?.pages[nextPage][nextPageRowIdx] || rows[nextIndex];
				}
			}

			// If extensionUrl is provided, we need to update the extension. This is to fix the
			// issue where inline updating Priority but the previous value is still displayed before the row data is refreshed
			if (extensionUrl) {
				const index = record?.extension?.findIndex(({ url }) => url === extensionUrl);
				if (
					index > -1 &&
					typeof value === 'object' &&
					record.extension[index]?.valueCoding?.display &&
					record.extension[index]?.valueCoding?.code
				) {
					record.extension[index].valueCoding.display = value?.display;
					record.extension[index].valueCoding.code = value?.code;
				}
			}

			// colName can be a complex path like extension[0].valueCoding.display
			_.set(record, colName, value);

			// not a big fan of this, but need to a mechanism to refresh once
			setForceRefresh(val => !val);
		};

		const insertUpdateRow = syncObject => {
			const rowIndex =
				syncObject.rowIndex !== undefined && syncObject.rowIndex !== null
					? syncObject.rowIndex
					: getRowIndexOf('id', syncObject.id);

			// Update existing row by new rowData
			if (rowIndex > -1) {
				let page;
				let pageRowIdx;
				// since the fist page contains dynamic length of data, we need to handle it seperatly
				if (rowIndex <= tableData.pages[0].length) {
					page = 0;
					pageRowIdx = rowIndex;
				} else {
					const rowIndexWithoutFirstPage = rowIndex - tableData.pages[0].length;
					page = Math.floor(rowIndexWithoutFirstPage / pageSize) + 1;
					pageRowIdx = rowIndexWithoutFirstPage % pageSize;
				}

				if (syncObject.data) {
					tableData.pages[page][pageRowIdx] = syncObject.data;
				}
				// remove the study if it's no longer need to be displayed
				else {
					tableData?.pages[page]?.splice(pageRowIdx, 1);
				}

				// not a big fan of this, but need to a mechanism to refresh once
				setForceRefresh(val => !val);
			}
			// Insert the new data at the first index of the table
			else if (syncObject.data && tableData?.pages[0] && syncObject.isAdding) {
				tableData.pages[0].unshift(syncObject.data);

				setForceRefresh(val => !val);
			}
		};

		/**
		 * Expand a row to show its subrows
		 * @param {string} rowIndex - The index of the row to expand
		 * @param {object} rowData - The data of the row to expand
		 * @param {object} cell - The cell that was clicked to expand the row
		 * @returns {void}
		 * */
		const expandRow = useCallback(
			async (rowIndex, rowData) => {
				// Get index of all row levels
				// Example: 1.2.3

				// add the row index to the expanded rows
				expandRowRef.current = [...expandRowRef.current, rowIndex];

				const indices = rowIndex.split('.').map(Number);
				if (!rowData?.subRows && indices.length > 0) {
					setIsRowLoading(true);
					const newFilters = [];

					// Get the page and pageRowIdx of the row
					const { page, pageRowIdx } = getPageInfo(indices[0].toString());
					let currentRowData = tableData.pages[page][pageRowIdx];
					const primaryRow = currentRowData;

					// Get the column props of the grouping row
					let columnProps = columns.findLast(col => col.name === groupingItems[0].id)?.header?.props;

					// Build the filter for the grouping row to fetch the subrows
					newFilters.push(bulidFilterForGroup(columnProps, currentRowData.rawData, groupingItems[0]));

					// Get all columns props and build the filters for the subrows
					for (let i = 1; i < indices.length; i += 1) {
						currentRowData = currentRowData.subRows[indices[i]];

						columnProps = columns.findLast(col => col.name === groupingItems[i]?.id)?.header?.props;
						newFilters.push(bulidFilterForGroup(columnProps, currentRowData.rawData, groupingItems[i]));
					}

					// Get the new grouping items
					// For the case mullti-level grouping, get the sub grouping items
					const newGroup = groupingItems.slice(indices.length);

					if (!currentRowData.subRows) {
						// Fetch the subrows
						const fetchedData = await onLoadDataRef?.current(0, pageSize, newFilters, newGroup);

						currentRowData.subRows = fetchedData;

						// If the fetched data is equal to the page size, then need to handle infinite scrolling
						if (fetchedData.length === pageSize) {
							setCurrentExpandedGroup([
								{
									rowIndex,
									rowData,
									groups: newGroup,
									filters: newFilters,
									endSubRowIndex: `${rowIndex}.${pageSize - 10}`, // Fetch the next page when the 10th row from bottom up is visible
								},
								...currentExpandedGroup,
							]);
						} else {
							// If the fetched data is less than the page size, no need to handle infinite scrolling
							currentRowData.hasFullSubRow = true;
						}

						insertUpdateRow({ rowIndex: indices[0], data: { ...primaryRow } });
					} else if (currentRowData.subRows && !currentRowData.hasFullSubRow) {
						// If the subrows are already fetched
						setCurrentExpandedGroup([
							{
								rowIndex,
								rowData,
								groups: newGroup,
								filters: newFilters,
								endSubRowIndex: `${rowIndex}.${pageSize - 1}`,
							},
							...currentExpandedGroup,
						]);
					}

					setIsRowLoading(false);
				}
			},
			[tableData, currentExpandedGroup]
		);

		const collapseRow = useCallback(rowIndex => {
			// Remove the row index from the expanded rows
			expandRowRef.current = expandRowRef.current.filter(row => row !== rowIndex);
			setCurrentExpandedGroup(prev => prev.filter(group => group.rowIndex !== rowIndex));
		}, []);

		const getPageInfo = rowIndex => {
			let page;
			let pageRowIdx;

			// since the fist page contains dynamic length of data, we need to handle it seperatly
			if (rowIndex <= tableData.pages[0].length) {
				page = 0;
				pageRowIdx = rowIndex;
			} else {
				const rowIndexWithoutFirstPage = rowIndex - tableData.pages[0].length;
				page = Math.floor(rowIndexWithoutFirstPage / pageSize) + 1;
				pageRowIdx = rowIndexWithoutFirstPage % pageSize;
			}

			return { page, pageRowIdx };
		};

		const batchInsertUpdateRows = syncArray => {
			let refresh = false;
			const addedRows = [];
			const deletedRows = [];
			syncArray.forEach(syncObject => {
				const rowIndex = getRowIndexOf('id', syncObject.id);
				// Update existing row by new rowData
				if (rowIndex > -1) {
					const { page, pageRowIdx } = getPageInfo(rowIndex);

					if (syncObject.data && !syncObject.isDeleting) {
						tableData.pages[page][pageRowIdx] = syncObject.data;
					}
					// remove the study if it's no longer need to be displayed
					else {
						deletedRows.push({ page, pageRowIdx, flatIndex: page * pageSize + pageRowIdx });
					}

					refresh = true;
				}
				// Insert the new data at the first index of the table
				else if (syncObject.data && tableData?.pages[0] && syncObject.isAdding) {
					addedRows.push(syncObject.data);
					refresh = true;
				}
			});

			if (deletedRows.length > 0) {
				// sort and delete rows in reverse order
				deletedRows.sort((a, b) => b.flatIndex - a.flatIndex);
				deletedRows.forEach(deletedRow => {
					tableData.pages[deletedRow.page]?.splice(deletedRow.pageRowIdx, 1);
				});
			}

			if (addedRows.length > 0) {
				addedRows.forEach(addedRow => {
					tableData.pages[0].unshift(addedRow);
				});
			}

			// // not a big fan of this, but need to a mechanism to refresh once
			if (refresh) {
				setForceRefresh(val => !val);
			}
		};

		// Sync data for grouping grid
		const batchInsertUpdateGroupRows = useCallback(
			async syncArray => {
				let primaryRow;
				let primaryRowIndex = -1;
				const removeList = [];

				syncArray.forEach(async syncObject => {
					// If the syncObject is deleting, remove it from the group
					if (syncObject.isDeleting) {
						removeList.push({ id: syncObject.id });
						return;
					}

					if (!syncObject.data) {
						return;
					}
					// Get current Column properties from Mapping (ImagingStudyWorklistMapping)
					const primaryColumnProps = columns.findLast(col => col.name === groupingItems[0].id)?.header?.props;

					// Find the primary row in the flatData
					flatData.some((row, index) => {
						if (compareGroupContent(row, syncObject.data, primaryColumnProps) === 0) {
							primaryRow = row;
							primaryRowIndex = index;
							return true; // Exit the iteration
						}
						return false;
					});

					// If the primary row is found, continue to find the sub rows
					if (primaryRowIndex > -1) {
						if (primaryRow.subRows) {
							let currentGroupRowData = primaryRow;
							let groupLevel = 0;

							// Use the same way to check the sub level groups
							for (let i = 1; i < groupingItems.length; i += 1) {
								const columnProps = columns.findLast(col => col.name === groupingItems[i].id)?.header
									?.props;

								if (currentGroupRowData.subRows) {
									currentGroupRowData = currentGroupRowData.subRows.find(
										row => compareGroupContent(row, syncObject.data, columnProps) === 0
									);

									if (currentGroupRowData) {
										groupLevel += 1;
									}
								}
							}

							// When found the Study reccord, update it
							const subRowIndex = currentGroupRowData?.subRows?.findIndex(
								sbRow => sbRow.id === syncObject.id
							);

							// If the row is found, update it
							if (subRowIndex > -1) {
								currentGroupRowData.subRows[subRowIndex] = syncObject.data;
							} else if (!currentGroupRowData) {
								// If the Sync Study is not found, remove it from the group
								removeList.push({ id: syncObject.id });
							} else {
								// If the Sync Study is not found, Sync Study it to this group
								// Before adding Sync Study, check if the Sync Study is already in other groups, if so, remove it from the other group
								removeContentGroupRows([{ id: syncObject.id }]);

								// Add the new row to the group
								if (currentGroupRowData.subRows) {
									currentGroupRowData.subRows.unshift(syncObject.data);
									currentGroupRowData.rawData[groupingItems[groupLevel].id].docCount += 1;
								}
							}
						} else {
							// If the primary row is found but no sub rows, clean up the sub rows
							removeList.push({ id: syncObject.id });
						}
					} else {
						// If the primary row is not found, remove the syncObject from the group
						removeList.push({ id: syncObject.id });
					}
				});

				// refresh the Group
				insertUpdateRow({ rowIndex: primaryRowIndex, data: { ...primaryRow } });

				removeContentGroupRows(removeList);
			},
			[tableData, flatData, columns, groupingItems]
		);

		// removeList is an array of objects with id of the records in last level of the group
		// removeContentGroupRows is used to remove the rows from the group
		const removeContentGroupRows = useCallback(
			removeList => {
				removeList.forEach(removeItem => {
					// Check the primary group level
					flatData.forEach((primaryRow, primaryRowIndex) => {
						if (removeItem.bypassGroupID !== primaryRow.id) {
							const found = getLastGroupByContentRecord(primaryRow, 0, removeItem, groupingItems);

							// If the row is found, update the primary row
							if (found) {
								found.groupRow.subRows.splice(found.subRowIndex, 1);
								found.groupRow.rawData[groupingItems[found.groupLevel].id].docCount -= 1;
								insertUpdateRow({ rowIndex: primaryRowIndex, data: { ...primaryRow } });
							}
						}
					});
				});
			},
			[flatData, groupingItems]
		);

		const removeRows = syncArray => {
			let shouldUpdate = false;

			for (const syncObject of syncArray) {
				const rowIndex = getRowIndexOf('id', syncObject.id);
				if (rowIndex <= -1) {
					continue;
				}

				const { page, pageRowIdx } = getPageInfo(rowIndex);
				tableData.pages[page].splice(pageRowIdx, 1);
				shouldUpdate = true;
			}

			if (shouldUpdate) {
				setForceRefresh(val => !val);
			}
		};
		const getLogData = () => flatData;

		useImperativeHandle(
			ref,
			() => ({
				updateData,
				getRowIndexOf,
				getRowData,
				insertUpdateRow,
				batchInsertUpdateRows,
				removeRows,
				handleExport,
				getLogData,
				batchInsertUpdateGroupRows,
			}),
			[tableData, flatData]
		);

		// a mechanism to invalidate the entire grid and re-fetch
		const refetchData = () => {
			refetch();
		};

		const loadMoreItems = useCallback(
			_.debounce(() => {
				fetchNextPage();
			}, 100),
			[fetchNextPage]
		);

		//called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
		const fetchMoreOnBottomReached = useCallback(
			containerRefElement => {
				if (containerRefElement) {
					const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

					if (!isFetching && !isRowLoading) {
						//once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
						if (scrollHeight - scrollTop - clientHeight < 300 && hasMoreData) {
							// Handle infinite scrolling for main grid
							loadMoreItems();
						} else if (
							mavenWorklistGrouping &&
							!isRowLoading &&
							currentExpandedGroup.length > 0 &&
							currentExpandedGroup[0].endSubRowIndex
						) {
							// Handle infinite scrolling for subrows
							const indexes = rowVirtualizer.getIndexes();

							// Check if the last row of the expanded group is visible
							const rowIndex = rows.findIndex(row => row.id === currentExpandedGroup[0].endSubRowIndex);

							if (indexes.indexOf(rowIndex) > -1 || containerRefElement.musLoaded) {
								fetchMoreDataGroup(currentExpandedGroup[0]);
							}
						}
					}
				}
			},
			[isFetching, hasMoreData, currentExpandedGroup, isRowLoading]
		);

		// Function to fetch more data for the expanded group
		const fetchMoreDataGroup = useCallback(
			async currentGroup => {
				const { rowIndex, rowData, groups, filters } = currentGroup;

				const indices = rowIndex.split('.').map(Number);
				if (!rowData?.subRows && indices.length > 0) {
					setIsRowLoading(true);
					const { page, pageRowIdx } = getPageInfo(indices[0].toString());
					let currentRowData = tableData.pages[page][pageRowIdx];
					const primaryRow = currentRowData;

					for (let i = 1; i < indices.length; i += 1) {
						currentRowData = currentRowData.subRows[indices[i]];
					}

					// Fetch the subrows
					const fetchedData = await onLoadDataRef?.current(
						currentRowData.subRows ? Math.ceil((currentRowData.subRows.length + 1) / pageSize) : 0,
						pageSize,
						filters,
						groups,
						currentRowData.subRows?.[currentRowData.subRows.length - 1]?.rawData?.afterKey ?? null
					);

					// Append the fetched data to the existing subrows
					// The record can be added from Twilio sync, so we need to check if the record is already in the subrows
					// If the record is already in the subrows, do not add it again
					const existingIds = new Set(currentRowData.subRows.map(row => row.id));
					currentRowData.subRows = [
						...currentRowData.subRows,
						...fetchedData.filter(newRow => !existingIds.has(newRow.id)),
					];

					if (fetchedData.length < pageSize) {
						// If the fetched data is less than the page size, the end of the subrows is reached
						currentRowData.hasFullSubRow = true;
						setCurrentExpandedGroup(prev => prev.filter(group => group.rowIndex !== rowIndex));
					} else {
						// If the fetched data is equal to the page size, the end of the subrows is not reached
						setCurrentExpandedGroup(prevState => {
							const index = prevState.findIndex(group => group.rowIndex === rowIndex);
							if (index === -1) return prevState;

							// Update the endSubRowIndex to fetch more data when the 10th row from bottom up is visible
							const updatedGroup = {
								...prevState[index],
								endSubRowIndex: `${rowIndex}.${currentRowData.subRows.length - 10}`,
							};
							const newState = [...prevState];
							newState[index] = updatedGroup;

							return newState;
						});
					}

					insertUpdateRow({ rowIndex: indices[0], data: { ...primaryRow } });

					setIsRowLoading(false);
				}
			},
			[tableData, getPageInfo]
		);

		//a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
		useEffect(() => {
			fetchMoreOnBottomReached(vRowRef.current);
		}, [fetchMoreOnBottomReached]);

		// Function to updtate the column width in the state
		const onColumnWidthChange = useCallback(
			(columnId, widthValue) => {
				const newColumnSizing = columnSizing ? { ...columnSizing } : {};

				newColumnSizing[columnId] = widthValue;
				setColumnSizing(newColumnSizing);
			},
			[columnSizing, setColumnSizing]
		);

		// onChange updates column size immediately, onEnd updates column size after
		// mouse release
		const columnResizeMode = 'onChange';
		const table = useReactTable({
			data: flatData,
			columns: cols,
			columnResizeMode,
			state: {
				columnOrder,
			},
			getExpandedRowModel: mavenWorklistGrouping ? getExpandedRowModel() : undefined,
			getSubRows: row => row.subRows,
			onColumnOrderChange: setColumnOrder,
			getCoreRowModel: getCoreRowModel(),
			meta: {
				updateData,
				refetchData,
				insertUpdateRow,
				batchInsertUpdateRows,
				removeRows,
			},
			debugTable: true,
		});

		const headerGroups = table.getHeaderGroups();

		useEffect(() => {
			if (command?.type === 'open study') {
				const record = flatData[0];
				const patientID = record?.patientID;
				const internalPatientId = record?.subject?.id;
				let studyInstanceUid = record?.identifier?.find(
					recordItem => recordItem.system === 'urn:dicom:uid'
				)?.value;
				studyInstanceUid = studyInstanceUid?.replace('urn:oid:', '');
				const issuerOfPatientId = record?.extension?.find(
					ext => ext.url == fhirExtensionUrls.organization.issuer
				)?.valueReference?.display;
				const internalStudyId = record?.id;
				const orderId = record?.basedOn?.[0]?.id;
				const internalManagingOrganizationID = record?.internalManagingOrganizationID;
				const { referringFacilityId } = record;
				const { documentViewerNavigateURL } = getViewerURLs({
					patientID,
					internalPatientId,
					orderId,
					internalStudyId,
					internalManagingOrganizationID,
					issuerOfPatientId,
					referringFacilityId,
					studyInstanceUid,
				});

				goTo.any(documentViewerNavigateURL);
				setCommand(null);
				setReceivedTranscription('[...opened]');
			}
		}, [command]);

		useEffect(() => {
			if (canResizeColumns) {
				// get the last visible header
				const flatHeaders = headerGroups?.map(headerGroup => headerGroup.headers)?.flat();
				let visibleColHeaders = flatHeaders?.filter((header, index) => cols[index].visible);

				// Calculate and set the width of the empty spacing column that is
				// placed at the very end of the grid.
				// The spacing column should expand to fill in the remaining space if the
				// width of the other columns + the minimum width of the spacing column
				// are not enough to fill it out. This is done so that the row
				// animations and effects always extend fully across the grid.
				let customLastWidth = 0;
				if (Array.isArray(visibleColHeaders) && visibleColHeaders.length > 0) {
					setLastColId(visibleColHeaders[visibleColHeaders.length - 1].id);

					// Exclude the width of first element, its width will always be 24px
					// as defined in DataGridColumnHeader
					// Exclude the last element, it is the spacingCol
					visibleColHeaders = visibleColHeaders?.slice(1, visibleColHeaders.length - 1);
					// each cell except the last cell has 20 horizontal padding
					const cellPadding = 20;
					let totalVisibleColumnWidth = 24 + cellPadding;
					visibleColHeaders.forEach(
						header => (totalVisibleColumnWidth = totalVisibleColumnWidth + header.getSize() + cellPadding)
					);
					// Add padding for the excluded last col
					totalVisibleColumnWidth += 20;

					// subtract 8 pixels from tableMeasurements to account for scrollbar
					if (tableMeasurements.width - 8 > totalVisibleColumnWidth) {
						const diff = tableMeasurements.width - 8 - totalVisibleColumnWidth;
						customLastWidth += diff;
					}
				}
				setLastColWidth(customLastWidth);

				// Check if the grid has finished rendering
				if (headerGroups?.length > 0 && headerGroups[0]?.headers?.length > 0) {
					setIsRendered(true);
				}
			}
		}, [tableMeasurements.width, headerGroups, canResizeColumns]);

		const onContextMenuClick = useCallback(
			(e, data, index) => {
				if (mavenEnableWorklistContextMenu) {
					e.preventDefault();
					onDataGridContextMenuClick(e, data, index);
					setRightClickedItem({ ...data, index });
					setPoints({
						x: e.pageX,
						y: e.pageY,
					});
				}
			},
			[mavenEnableWorklistContextMenu, onDataGridContextMenuClick]
		);

		const onContextMenuClose = useCallback(() => {
			toggleStudyPresence({ id: rightClickedItem.id, data: rightClickedItem });
			setRightClickedItem(null);
		}, [rightClickedItem, toggleStudyPresence]);

		// Virtualizing
		const { rows } = table.getRowModel();

		// useVirtualizer is tanstack/react-virtual libray that takes care of the virtualization
		const rowVirtualizer = useVirtualizer({
			count: rows.length,
			getScrollElement: useCallback(() => vRowRef.current, []),
			estimateSize: () => 30, // estimate size of each row, smaller number means more rows are rendered
			overscan: 25, // number of rows to render above and below the visible area, bigger means slower scrolling but less flickering
		});

		const items = rowVirtualizer.getVirtualItems();

		// before and after are needed for padding the top and bottom of the virtulualized table to make header sticky
		const [before, after] =
			items.length > 0
				? [
						notUndefined(items[0]).start - rowVirtualizer.options.scrollMargin,
						rowVirtualizer.getTotalSize() - notUndefined(items[items.length - 1]).end,
				  ]
				: [0, 0];

		return (
			<>
				<DndProvider backend={HTML5Backend}>
					{isLoadingRef || isLoading || isFetchingNextPage ? (
						<LinearProgress
							sx={{
								position: tableHeaderRef?.current ? 'absolute' : 'relative',
								top:
									tableHeaderRef?.current?.offsetTop + tableHeaderRef?.current?.offsetHeight ||
									'unset',
								zIndex: '2',
								width: tableContainerRef?.current?.offsetWidth,
							}}
						/>
					) : (
						''
					)}
					<CSVLink
						ref={csvLinkRef}
						data={exportData}
						filename={`${name?.charAt(0)?.toUpperCase() + name?.slice(1)} - ${new Date()
							.toLocaleDateString()
							.replaceAll('/', '-')}.csv`}
						headers={exportHeader}
					/>
					<TableContainer
						ref={mergeRefs([tableContainerRef, tableMeasurementsRef])}
						className="datagrid"
						data-testid="data-grid-table-container"
						sx={{
							width: 'unset',
							height,
						}}
						onScroll={e => {
							// Save scroll position on scroll
							onScrollChange({
								scrollTop: e.target.scrollTop,
								scrollLeft: e.target.scrollLeft,
							});
						}}
					>
						{
							<div
								ref={vRowRef} // useVirtualizer uses this ref to calculate scrolling and what items to render
								data-testid="data-grid-table-vrow"
								style={{ overflow: 'auto', height: '100%', overflowAnchor: 'none' }}
								onScroll={e => {
									fetchMoreOnBottomReached(e.target);

									// Save scroll position on scroll
									onScrollChange({
										scrollTop: e.target.scrollTop,
										scrollLeft: e.target.scrollLeft,
									});
								}}
							>
								<Table
									stickyHeader
									data-cy="study-status-table"
									{...(canResizeColumns || forceFixed ? { sx: { tableLayout: 'fixed' } } : null)}
								>
									<TableHead
										ref={tableHeaderRef}
										data-testId={`${name}-data-grid-table-header`}
										sx={{
											position: 'sticky',
											top: 0,
											zIndex: 1,
											...subRowSX,
										}}
									>
										{headerGroups.map(headerGroup => (
											<TableRow
												key={headerGroup.id}
												sx={{
													'& .MuiTableCell-root': {
														color: 'rgba(255, 255, 255, 0.87)',
														backgroundColor: 'rsSecondary.medium',
													},
												}}
											>
												{headerGroup.headers
													.map(
														(header, index) =>
															cols[index].visible && (
																<MemoizedDataGridColumnHeaderV2
																	key={header.id}
																	canResizeColumns={
																		canResizeColumns &&
																		!header.column?.columnDef?.disableResizing
																	}
																	cellSx={{
																		':nth-last-of-type(2)': {
																			'.resizer': {
																				opacity: secondLastChildResizerOpacity,
																			},
																		},
																	}}
																	cols={cols}
																	columnResizeMode={columnResizeMode}
																	draggableTable={table}
																	enableColumnDnd={
																		cols[index].draggable && enableColumnDnd
																	}
																	flexRender={flexRender}
																	header={header}
																	index={index}
																	lastColWidth={lastColWidth}
																	rows={
																		gridCustomHeader &&
																		header.id.includes('flagCol')
																			? rows
																			: []
																	}
																	setCurrWorklistColumns={setCurrWorklistColumns}
																	onColumnWidthChange={onColumnWidthChange}
																	onMouseOut={
																		lastColId === header?.id
																			? () => setSecondLastChildResizerOpacity(0)
																			: null
																	}
																	onMouseOver={
																		lastColId === header?.id
																			? () => setSecondLastChildResizerOpacity(1)
																			: null
																	}
																/>
															)
													)
													?.filter(component => component)}
											</TableRow>
										))}
										{mavenWorklistGrouping && name === 'worklist' && (
											<TableRow>
												<TableCell
													colSpan={cols.length}
													sx={{
														background: 'rgba(65, 65, 65, 1)',
														padding: 0,
														height: '44px',
													}}
												>
													<GroupingDropable columns={columns} name="grouping" />
												</TableCell>
											</TableRow>
										)}
									</TableHead>

									<TableBody
										key={items[0]?.key}
										onContextMenu={e => {
											mavenEnableWorklistContextMenu && e.preventDefault();
										}}
									>
										{before > 0 && ( // padding for the top of the table, needed to make table header sticky
											<tr>
												<td colSpan={cols.length} style={{ height: before }} />
											</tr>
										)}
										{items.map(virtualRow => {
											const row = rows[virtualRow.index];
											const { index } = virtualRow;
											return (
												<DataRow
													key={row.original?.id || row.id}
													canResizeColumns={canResizeColumns}
													cellSx={cellSx}
													cellsPadding={cellsPadding}
													collapseRow={collapseRow}
													columnOrder={columnOrder}
													enableRowDnd={enableRowDnd}
													expandRow={expandRow}
													expandedRows={expandRowRef.current}
													flexRender={flexRender}
													gridName={name}
													groupingItems={groupingItems}
													isExpanded={index === expandedRowIndex}
													isRowLoading={isRowLoading}
													mavenWorklistGrouping={mavenWorklistGrouping}
													rowIndex={index}
													selectedRow={[selectedRow, ...selectedResourcesRowsIds]?.includes(
														Number(row.id)
													)}
													size={virtualRow.size}
													studyStatusColor={
														rowColorStudyStatus ? row.original.studyStatusColor : null
													}
													subRow={subRow}
													tableRow={row}
													visitedWorklist={visitedWorklist}
													onContextMenuClick={onContextMenuClick}
													onRowClick={onRowClick}
													onRowDoubleClick={onRowDoubleClick}
												/>
											);
										})}
										{after > 0 && ( // padding for the bottom of the table, needed to make table header sticky
											<tr>
												<td colSpan={cols.length} style={{ height: after }} />
											</tr>
										)}
										{rows?.length === 0 && emptyMessage && (
											<TableRow>
												<TableCell colSpan={12} sx={{ textAlign: 'center' }}>
													{emptyMessage}
												</TableCell>
											</TableRow>
										)}
									</TableBody>
									{footer && (
										<TableFooter>
											<TableCell colSpan={12} sx={{ padding: 0 }}>
												{footer}
											</TableCell>
										</TableFooter>
									)}
								</Table>
							</div>
						}
					</TableContainer>
					{mavenWorklistGrouping && <DataGridColumnDragLayer />}
				</DndProvider>
				{mavenEnableWorklistContextMenu && (
					<WorklistContextMenu
						points={points}
						rightClickedItem={rightClickedItem}
						setPoints={setPoints}
						updateData={updateData}
						wheelMenuItems={wheelMenuItems}
						onContextMenuClose={onContextMenuClose}
						onWheelMenuItemClick={handleWheelClick}
					/>
				)}
			</>
		);
	}
);

const DataRow = ({ mavenWorklistGrouping, ...props }) => {
	if (mavenWorklistGrouping) {
		return <MemoizedDataGridRowGroup {...props} />;
	}
	return <DataGridRow {...props} />;
};

DataGrid.propTypes = {
	/**
	 * Name of the table, which will be used as a caching key so please ensure that it is unique system wide.
	 */
	name: PropTypes.string.isRequired,
	/**
	 * Column definition for the grid
	 */
	columns: PropTypes.array,
	/**
	 * Another potential parameter to be used as part of the queryKey in the useInfiniteQuery.
	 */
	cacheKey: PropTypes.string,
	/**
	 * Data to be displayed in the grid.
	 */
	data: PropTypes.array,
	/**
	 * Max records to fetch per page
	 */
	pageSize: PropTypes.number,
	/**
	 * Height of the datagrid
	 */
	height: PropTypes.string,
	/**
	 * Indicates whether data is in the process of loading.
	 */
	isLoadingRef: PropTypes.bool,
	/**
	 * Determines if the columns can be resized or not.
	 */
	canResizeColumns: PropTypes.bool,
	/**
	 * Optional - lifted state object with column ids as keys and column widths (numbers) as values. This should be passed in if canResizeColumns is true
	 */
	columnSizing: PropTypes.object,
	/**
	 * Optional - Setter function for columnSizing. Should be passed in columnSizing
	 * is passed in
	 */
	setColumnSizing: PropTypes.func,
	/**
	 * Function to call to get the next page of data.
	 */
	onLoadData: PropTypes.func,
	/**
	 * Handler for when a row is clicked. Passes back the row record for processing.
	 */
	onRowClick: PropTypes.func,
	/**
	 * Determines if row DnD shall be enabled
	 */
	enableRowDnd: PropTypes.bool,
	/**
	 * Determines if column DnD shall be enabled
	 */
	enableColumnDnd: PropTypes.bool,
	/**
	 * Provides the selected row index to the grid
	 */
	selectedRow: PropTypes.number,
	/**
	 * Provides the selected resources to be potentially stick to the top of grid
	 */
	selectedResources: PropTypes.array,
	/**
	 * Flag to watch filters changes to manage selected items at the top of the table
	 */
	shouldUpdateAfterFiltersChanged: PropTypes.bool,
	/**
	 * Function to let the parent layer know that the changes triggered by filters changes were applied
	 */
	onUpdateAfterFiltersChanged: PropTypes.func,
	/**
	 * css properties to apply on tabel cell
	 */
	cellSx: PropTypes.object,
	/**
	 * Determines if we want to force the table to be fixed, which sets the tableLayout to "fixed".
	 * When set to 'fixed', the table layout algorithm assigns a fixed width to each column based on the width of the first row of cells,
	 * and does not adjust the column widths based on the content of the cells.
	 */
	forceFixed: PropTypes.bool,
	/**
	 * function to enable or disable download button, will enable download button after data is loaded
	 */
	setEnableDownloadButton: PropTypes.func,
	/**
	 * Function to set the current the scroll position
	 */
	onScrollChange: PropTypes.func,
	/**
	 * The scroll position
	 */
	scrollPosition: PropTypes.shape({
		scrollTop: PropTypes.number,
		scrollLeft: PropTypes.number,
	}),
};

DataGrid.defaultProps = {
	pageSize: 50,
	nexPageSize: 30,
	canResizeColumns: false,
	isLoadingRef: false,
	enableRowDnd: true,
	enableColumnDnd: true,
	forceFixed: false,
	onScrollChange: () => {},
	scrollPosition: {
		scrollTop: 0,
		scrollLeft: 0,
	},
	columnSizing: {},
};

DataGrid.displayName = 'DataGrid';

export const MemoizedDataGrid = memo(DataGrid);

export default DataGrid;
